import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from 'lucide-react';

// Toast — v2 design (2026-05-30).
//
// Four kinds, each a colored card with icon + title + optional subtitle +
// close button. Stacked top-down on the right, slides in from the right,
// auto-dismisses after 4s.
//
// API is backwards-compatible with the previous single-line toast:
//   showToast('Hello')                                  // info, no subtitle
//   showToast('Saved', 'success')                       // success, no subtitle
//   showToast('Profile updated', 'success', {           // with subtitle
//     subtitle: 'Your changes have been saved',
//   })
//
// The big dark dynamic-island ("Profile updated") at the top of the
// screen is a separate component (DynamicIsland) and is unaffected by
// this change.

type ToastType = 'success' | 'info' | 'warning' | 'error';

interface ToastMessage {
  id: number;
  title: string;
  subtitle?: string;
  type: ToastType;
}

interface ToastOptions {
  subtitle?: string;
}

let toastIdCounter = 0;
let addToastFn:
  | ((title: string, type?: ToastType, options?: ToastOptions) => void)
  | null = null;

// Backwards-compatible signature: `type` accepts the old union plus the
// new "warning" value. Existing call sites need no change.
export const showToast = (
  title: string,
  type: ToastType = 'info',
  options: ToastOptions = {},
) => {
  if (addToastFn) {
    addToastFn(title, type, options);
  }
};

const AUTO_DISMISS_MS = 4000;

const Toast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback(
    (title: string, type: ToastType = 'info', options: ToastOptions = {}) => {
      const id = ++toastIdCounter;
      setToasts((prev) => [...prev, { id, title, subtitle: options.subtitle, type }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, AUTO_DISMISS_MS);
    },
    [],
  );

  useEffect(() => {
    addToastFn = addToast;
    return () => {
      addToastFn = null;
    };
  }, [addToast]);

  const dismiss = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <>
      <style>{toastStyles}</style>
      <div
        className="fixed z-[60] pointer-events-none flex flex-col gap-2.5 safe-area-top"
        style={{
          top: 'env(safe-area-inset-top, 16px)',
          right: 12,
          left: 12,
          maxWidth: 380,
          marginLeft: 'auto',
        }}
        role="region"
        aria-label="Notifications"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </>
  );
};

interface ToastCardProps {
  toast: ToastMessage;
  onDismiss: () => void;
}

const ToastCard: React.FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const variant = VARIANTS[toast.type];
  const Icon = variant.icon;

  return (
    <div
      className="toast-card pointer-events-auto rounded-2xl px-4 py-3.5 flex items-start gap-3 shadow-[0_20px_50px_-10px_rgba(15,23,42,0.45)]"
      style={{
        background: variant.background,
        border: `1px solid ${variant.border}`,
        color: variant.textPrimary,
      }}
      role="status"
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: variant.iconBackground }}
      >
        <Icon
          className="w-[18px] h-[18px]"
          style={{ color: variant.iconColor }}
          strokeWidth={2.4}
        />
      </div>

      <div className="flex-1 min-w-0 pt-0.5">
        <p
          className="text-[14px] font-semibold leading-snug"
          style={{ color: variant.textPrimary }}
        >
          {toast.title}
        </p>
        {toast.subtitle && (
          <p
            className="text-[12.5px] mt-0.5 leading-snug"
            style={{ color: variant.textSecondary }}
          >
            {toast.subtitle}
          </p>
        )}
      </div>

      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors hover:bg-white/10"
      >
        <X className="w-[14px] h-[14px]" style={{ color: variant.dismissColor }} />
      </button>
    </div>
  );
};

/* ----------------------------------------------------------------- */

interface Variant {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties; strokeWidth?: number }>;
  // Card background gradient + text colors — mirrors Mike's reference.
  // Each variant is a dark slate base with a thin colored accent so the
  // icon tile + title carry the meaning, not a flood of color.
  background: string;
  border: string;
  iconBackground: string;
  iconColor: string;
  textPrimary: string;
  textSecondary: string;
  dismissColor: string;
}

const VARIANTS: Record<ToastType, Variant> = {
  success: {
    icon: CheckCircle2,
    background: 'linear-gradient(135deg, #1F2A1F 0%, #14201A 100%)',
    border: 'rgba(34, 197, 94, 0.25)',
    iconBackground: 'rgba(34, 197, 94, 0.18)',
    iconColor: '#22C55E',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.65)',
    dismissColor: 'rgba(255, 255, 255, 0.6)',
  },
  info: {
    icon: Info,
    background: 'linear-gradient(135deg, #1A1F2D 0%, #141A28 100%)',
    border: 'rgba(59, 130, 246, 0.25)',
    iconBackground: 'rgba(59, 130, 246, 0.18)',
    iconColor: '#3B82F6',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.65)',
    dismissColor: 'rgba(255, 255, 255, 0.6)',
  },
  warning: {
    icon: AlertTriangle,
    background: 'linear-gradient(135deg, #2A2218 0%, #221A11 100%)',
    border: 'rgba(245, 158, 11, 0.25)',
    iconBackground: 'rgba(245, 158, 11, 0.18)',
    iconColor: '#F59E0B',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.65)',
    dismissColor: 'rgba(255, 255, 255, 0.6)',
  },
  error: {
    icon: XCircle,
    background: 'linear-gradient(135deg, #2A1818 0%, #221111 100%)',
    border: 'rgba(239, 68, 68, 0.25)',
    iconBackground: 'rgba(239, 68, 68, 0.18)',
    iconColor: '#EF4444',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.65)',
    dismissColor: 'rgba(255, 255, 255, 0.6)',
  },
};

const toastStyles = `
  @keyframes toast-slide-in {
    0%   { opacity: 0; transform: translateX(20px) scale(0.96); }
    100% { opacity: 1; transform: translateX(0)    scale(1);    }
  }
  .toast-card {
    animation: toast-slide-in 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @media (prefers-reduced-motion: reduce) {
    .toast-card { animation: none; }
  }
`;

export default Toast;
